/**
 * Node Template Enhancer
 * Enhances NodeTemplates by merging them with their corresponding
 * NodeType and NodeCategory data
 */

import type { NodeType } from '../Domain/NodeType';
import type { NodeCategory } from '../Domain/NodeCategory';
import type { NodeTemplate } from '../Domain/NodeTemplate';

export class NodeTemplateEnhancer {
  /**
   * Enhance templates with NodeType and NodeCategory references
   * Also fills missing properties from NodeType
   */
  enhance(
    templates: NodeTemplate[],
    nodeTypes: NodeType[],
    categories: NodeCategory[]
  ): NodeTemplate[] {
    const nodeTypeMap = new Map<string, NodeType>(nodeTypes.map(nt => [nt.code, nt]));
    const categoryMap = new Map<string, NodeCategory>(categories.map(cat => [cat.code, cat]));

    return templates.map(template => {
      const enhanced: NodeTemplate = {
        ...template,
        extensionId: template.extensionId ?? 1,
      };

      // ── Level 2: NodeType cascade ───────────────────────────────────────────
      // Use nodeTypeCode (shared nodeType) if set, else fall back to own code.
      // This lets multiple templates share one NodeType definition.
      const ntCode = template.nodeTypeCode || template.code;
      const nodeType = nodeTypeMap.get(ntCode);
      if (nodeType) {
        enhanced.nodeType = nodeType;
        if (!enhanced.extensionId && nodeType.extensionId) enhanced.extensionId = nodeType.extensionId;
        if (!enhanced.displayName) enhanced.displayName = nodeType.displayName;
        if (!enhanced.description) enhanced.description = nodeType.description;
        if (!enhanced.color) enhanced.color = nodeType.color;
        if (!enhanced.iconUrl) enhanced.iconUrl = nodeType.iconUrl;
        if (!enhanced.defaultInputPorts?.length) enhanced.defaultInputPorts = nodeType.defaultInputPorts;
        if (!enhanced.defaultOutputPorts?.length) enhanced.defaultOutputPorts = nodeType.defaultOutputPorts;
        if (!enhanced.shortDescription) enhanced.shortDescription = nodeType.shortDescription;
        if (!enhanced.longDescription) enhanced.longDescription = nodeType.longDescription;
        if (!enhanced.imageUrl) enhanced.imageUrl = nodeType.imageUrl;
        if (!enhanced.helpUrl) enhanced.helpUrl = nodeType.helpUrl;
      }

      // ── Level 3: NodeCategory cascade ──────────────────────────────────────
      // Fill anything still missing from the category — color, icon, ports,
      // credential types. Category is the widest fallback.
      const category = categoryMap.get(template.category);
      if (category) {
        enhanced.nodeCategory = category;
        if (!enhanced.color) enhanced.color = category.color;
        if (!enhanced.iconUrl) enhanced.iconUrl = category.iconUrl;
        if (!enhanced.defaultInputPorts?.length && category.defaultInputPorts?.length) {
          enhanced.defaultInputPorts = category.defaultInputPorts;
        }
        if (!enhanced.defaultOutputPorts?.length && category.defaultOutputPorts?.length) {
          enhanced.defaultOutputPorts = category.defaultOutputPorts;
        }
      }

      // Ensure port arrays are never undefined
      if (!enhanced.defaultInputPorts) enhanced.defaultInputPorts = [];
      if (!enhanced.defaultOutputPorts) enhanced.defaultOutputPorts = [];

      return enhanced;
    });
  }
}
